import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { Box, ListItem, ListItemText, Popover, Divider, Typography, useTheme } from '@mui/material'

import { get } from 'utils/lodash'
import { sortByDateSelector } from 'data/utils/sortByDateSelector'
import {
  useGetRentalFeesResetHistoryQuery,
  useGetRentalFeesByConfiguredServiceQuery,
} from 'api/configured-service/configuredServiceRentalFeesCrud'
import { useGetAllRentalPeriodVersionsQuery, useGetRentalFeeToConfiguredServiceVersionsQuery } from 'api/entity-version/entityVersionCrud'
import { HHAlert } from 'components/common/HHAlert'

import T from 'T'
import GracePeriodHistory from './GracePeriodHistory'
import { getRows, normalizeAllPeriodVersionsToRows, normalizePriceChangeVersionsToRows } from './utils'

const MAX_ROWS = 5

const GracePeriodResetHistoryPopover = ({ anchorEl, onClose, rows: controlledRows, isLoading: controlledLoading, configuredServiceId }) => {
  const isOpen = Boolean(anchorEl)
  const isControlled = controlledRows !== undefined && controlledLoading !== undefined
  const shouldFetch = !isControlled && isOpen && !!configuredServiceId
  const theme = useTheme()

  const {
    isFetching: isResetLoading,
    isError: isResetError,
    data: resetData,
  } = useGetRentalFeesResetHistoryQuery({ id: configuredServiceId }, { skip: !shouldFetch })

  const { data: rentalFeeData, isError: isRentalFeeError } = useGetRentalFeesByConfiguredServiceQuery(
    { id: configuredServiceId },
    { skip: !shouldFetch }
  )
  const rentalFeeToConfiguredServiceId = get(rentalFeeData, 'configuredRentalFees[0].id')

  const {
    isFetching: isPeriodVersionsLoading,
    isError: isPeriodVersionsError,
    data: allPeriodVersionsData,
  } = useGetAllRentalPeriodVersionsQuery({ rentalFeeToConfiguredServiceId }, { skip: !shouldFetch || !rentalFeeToConfiguredServiceId })

  const {
    isFetching: isPriceChangeLoading,
    isError: isPriceChangeError,
    data: priceChangeVersionsData,
  } = useGetRentalFeeToConfiguredServiceVersionsQuery(
    { rentalFeeToConfiguredServiceId },
    { skip: !shouldFetch || !rentalFeeToConfiguredServiceId, refetchOnMountOrArgChange: true }
  )

  const hasQueryError = isResetError || isRentalFeeError || isPeriodVersionsError || isPriceChangeError

  const graceRows = useMemo(() => getRows(resetData).map(row => ({ ...row, type: 'grace_reset' })), [resetData])
  const periodVersionRows = useMemo(() => normalizeAllPeriodVersionsToRows(allPeriodVersionsData), [allPeriodVersionsData])
  const priceChangeRows = useMemo(() => normalizePriceChangeVersionsToRows(priceChangeVersionsData), [priceChangeVersionsData])
  const mergedRows = useMemo(() => {
    const merged = [...graceRows, ...periodVersionRows, ...priceChangeRows]
    return sortByDateSelector({ data: merged, key: 'createdAt' })
  }, [graceRows, periodVersionRows, priceChangeRows])

  const displayRows = useMemo(() => {
    const source = isControlled ? controlledRows : mergedRows
    return Array.isArray(source) ? source.slice(0, MAX_ROWS) : []
  }, [isControlled, controlledRows, mergedRows])
  const effectiveLoading = isControlled ? controlledLoading : isResetLoading || isPeriodVersionsLoading || isPriceChangeLoading
  const extraRows = useMemo(() => {
    const source = isControlled ? controlledRows : mergedRows
    return Array.isArray(source) ? Math.max(0, source.length - MAX_ROWS) : 0
  }, [isControlled, controlledRows, mergedRows])

  return (
    <Popover
      sx={{ pointerEvents: 'none', maxWidth: '500px' }}
      slotProps={{ paper: { sx: { minWidth: 300, width: 'max-content' } } }}
      open={isOpen}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={onClose}
      disableRestoreFocus
    >
      <ListItem divider>
        <ListItemText
          primary={T.RENTAL_PERIOD_ACTIVITY}
          slotProps={{
            primary: { variant: 'h6', color: 'textSecondary' },
          }}
        />
      </ListItem>
      {!isControlled && hasQueryError ? (
        <Box p={2}>
          <HHAlert severity="error" borderColor={theme.palette.error.light}>
            {T.UNABLE_TO_LOAD_RENTAL_PERIOD_ACTIVITY}
          </HHAlert>
        </Box>
      ) : (
        <>
          <GracePeriodHistory isLoading={effectiveLoading} rows={displayRows} />
          {extraRows > 0 && (
            <>
              <Divider />
              <Typography m={2} variant="caption" display="flex" justifyContent="end">
                {`+${extraRows} more`}
              </Typography>
            </>
          )}
        </>
      )}
    </Popover>
  )
}

GracePeriodResetHistoryPopover.propTypes = {
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  rows: PropTypes.array,
  isLoading: PropTypes.bool,
  configuredServiceId: PropTypes.string,
}

export default GracePeriodResetHistoryPopover
